// Shared Trade Offer error formatter. Three surfaces render offer failures — the
// accept path on the review screen, the builder's commitment conflict, and (later)
// stale trade suggestions — and they must read consistently. Parse here, once.

import {
  OfferExceedsWantQuantityDetails,
  OfferInsufficientFundingDetails,
  OfferItemUnavailableDetails,
} from 'types/interfaces/tradeOffer';

export type OfferErrorKind =
  | 'item_unavailable'
  | 'insufficient_funding'
  | 'accept_unavailable'
  | 'condition_not_accepted'
  | 'exceeds_want_quantity'
  | 'expired'
  | 'not_pending'
  | 'forbidden'
  | 'rate_limited'
  | 'blocked'
  | 'private_have_list'
  | 'recipient_offers_disabled'
  | 'proposer_offers_disabled'
  | 'unknown';

export interface ParsedOfferError {
  kind: OfferErrorKind;
  code: string | null;
  /** User-facing message, already formatted */
  message: string;
  /** The offer is dead (failed); show the outcome, offer no retry */
  terminal: boolean;
  /** Transient (accept unavailable during rollout); safe to retry later, never map to failed copy */
  retryable: boolean;
  unavailable?: OfferItemUnavailableDetails;
  funding?: OfferInsufficientFundingDetails;
  /**
   * Carried so the builder can mark the offending LINE. The name alone is not
   * enough to identify it: the same card in three sets is three lines.
   */
  exceedsWant?: OfferExceedsWantQuantityDetails;
}

type ErrorLike = {
  status?: number | string;
  data?: {
    code?: unknown;
    message?: unknown;
    details?: unknown;
  };
};

const CONDITION_SHORT: Record<string, string> = {
  NEAR_MINT: 'NM',
  SLIGHTLY_PLAYED: 'LP',
  MODERATELY_PLAYED: 'MP',
  HEAVILY_PLAYED: 'HP',
  DAMAGED: 'DMG',
};

const LANGUAGE_LABELS: Record<string, string> = {
  en: 'English',
  es: 'Spanish',
  fr: 'French',
  de: 'German',
  it: 'Italian',
  pt: 'Portuguese',
  ja: 'Japanese',
  ko: 'Korean',
  ru: 'Russian',
  zhs: 'Chinese Simplified',
  zht: 'Chinese Traditional',
  he: 'Hebrew',
  la: 'Latin',
  grc: 'Ancient Greek',
  ar: 'Arabic',
  sa: 'Sanskrit',
  ph: 'Phyrexian',
};

export const formatConditionShort = (condition: string): string =>
  CONDITION_SHORT[condition] || condition;

export const formatLanguage = (lang: string): string => LANGUAGE_LABELS[lang] || lang;

/** "Rampant Growth (PW23 #9, NM, English, Foil)" — the one card-identity format all three failure surfaces use */
export const formatUnavailableCard = (d: OfferItemUnavailableDetails): string => {
  const parts = [
    `${d.setCode.toUpperCase()} #${d.collectorNumber}`,
    formatConditionShort(d.condition),
    formatLanguage(d.language),
  ];
  if (d.foil) parts.push('Foil');
  return `${d.cardName} (${parts.join(', ')})`;
};

export const formatItemUnavailableMessage = (d: OfferItemUnavailableDetails): string =>
  `${formatUnavailableCard(d)} is no longer available. This offer has been cancelled and no trade was created.`;

// The suggestion surfaces share the card-naming half with the accept 409 and
// the builder conflict message (one formatter, three failure points); only the
// consequence clause differs, because no offer existed yet to cancel.
export const formatSuggestionUnavailableMessage = (d: OfferItemUnavailableDetails): string =>
  `${formatUnavailableCard(d)} is no longer available. This suggestion has been removed and its other cards are released for other traders.`;

/** A validate failure without details (raced dismiss/expiry on another device) */
export const SUGGESTION_GONE_MESSAGE =
  'This suggestion is no longer available. Its cards are released for other traders.';

const isUnavailableDetails = (v: unknown): v is OfferItemUnavailableDetails =>
  !!v && typeof v === 'object' && typeof (v as OfferItemUnavailableDetails).cardName === 'string';

const isExceedsWantDetails = (v: unknown): v is OfferExceedsWantQuantityDetails =>
  !!v &&
  typeof v === 'object' &&
  typeof (v as OfferExceedsWantQuantityDetails).cardName === 'string' &&
  typeof (v as OfferExceedsWantQuantityDetails).remainingWantQuantity === 'number';

/**
 * "They only want 1 more copy of Armillary Sphere, and you've added 3."
 *
 * Three branches because "0 more copies" reads badly: at zero the sentence
 * becomes "they don't want any more", which is the case the reported bug
 * actually produces. Plain literals, not glossary constants — this names a
 * card and a quantity, never the feature (see utils/glossary.ts).
 */
export const formatExceedsWantQuantityMessage = (
  d: OfferExceedsWantQuantityDetails
): string => {
  const { cardName, offeredQuantity, remainingWantQuantity: remaining } = d;
  if (remaining <= 0) {
    return `They don't want any more copies of ${cardName}. Remove it to send this offer.`;
  }
  const copies = remaining === 1 ? '1 more copy' : `${remaining} more copies`;
  return `They only want ${copies} of ${cardName}, and you've added ${offeredQuantity}. Remove the extras to send this offer.`;
};

const isFundingDetails = (v: unknown): v is OfferInsufficientFundingDetails =>
  !!v &&
  typeof v === 'object' &&
  typeof (v as OfferInsufficientFundingDetails).yourShortfall === 'number';

const asString = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const GENERIC_FALLBACK = 'Something went wrong. Please try again.';

export const parseOfferError = (error: unknown): ParsedOfferError => {
  const base: ParsedOfferError = {
    kind: 'unknown',
    code: null,
    message: GENERIC_FALLBACK,
    terminal: false,
    retryable: false,
  };
  if (!error || typeof error !== 'object') return base;

  const err = error as ErrorLike;
  const code = asString(err.data?.code);
  const backendMessage = asString(err.data?.message);
  // The backend emits detail fields at the top level of the error body
  // (AppError spread); accept a nested details object too for safety.
  const details = err.data?.details ?? err.data;
  base.code = code;
  if (backendMessage) base.message = backendMessage;

  switch (code) {
    case 'OFFER_ITEM_UNAVAILABLE': {
      const unavailable = isUnavailableDetails(details) ? details : undefined;
      return {
        ...base,
        kind: 'item_unavailable',
        terminal: true,
        unavailable,
        message: unavailable ? formatItemUnavailableMessage(unavailable) : base.message,
      };
    }
    case 'OFFER_INSUFFICIENT_FUNDING':
      // Offer stays pending — a temporary state, not a failure
      return {
        ...base,
        kind: 'insufficient_funding',
        funding: isFundingDetails(details) ? details : undefined,
      };
    case 'CONDITION_NOT_ACCEPTED': {
      // 400 at create, or at accept after a want-record edit; the offer (if
      // one exists) stays pending, so this is never terminal.
      const d = details as { cardName?: unknown; acceptedConditions?: unknown } | undefined;
      const cardName = d && typeof d.cardName === 'string' ? d.cardName : null;
      const accepted =
        d && Array.isArray(d.acceptedConditions)
          ? (d.acceptedConditions as string[]).map(formatConditionShort).join(', ')
          : null;
      return {
        ...base,
        kind: 'condition_not_accepted',
        message:
          cardName && accepted
            ? `${cardName}: they accept ${accepted} only for this card.`
            : backendMessage || 'They no longer accept that condition for one of these cards.',
      };
    }
    case 'OFFER_EXCEEDS_WANT_QUANTITY': {
      // 400 at CREATION, so no offer exists: never terminal (there is nothing
      // to have failed) and never retryable (retrying the same package fails
      // identically). The remedy is to remove a card, which is why the copy
      // says so and why the details carry the have-row id.
      const exceedsWant = isExceedsWantDetails(details) ? details : undefined;
      return {
        ...base,
        kind: 'exceeds_want_quantity',
        exceedsWant,
        message: exceedsWant
          ? formatExceedsWantQuantityMessage(exceedsWant)
          : backendMessage || "You've added more copies of a card than they want.",
      };
    }
    case 'OFFER_ACCEPT_UNAVAILABLE':
      return {
        ...base,
        kind: 'accept_unavailable',
        retryable: true,
        message:
          "Accepting offers isn't available right now. Your offer is still open, so please try again soon.",
      };
    case 'OFFER_EXPIRED':
      return {
        ...base,
        kind: 'expired',
        terminal: true,
        message: backendMessage || 'This offer has expired.',
      };
    case 'OFFER_NOT_PENDING':
      return {
        ...base,
        kind: 'not_pending',
        message: backendMessage || 'This offer has already been responded to.',
      };
    case 'OFFER_FORBIDDEN':
      return {
        ...base,
        kind: 'forbidden',
        message: backendMessage || "You can't perform this action on this offer.",
      };
    case 'RATE_LIMITED':
      return {
        ...base,
        kind: 'rate_limited',
        message: backendMessage || "You've sent a lot of offers recently. Please try again later.",
      };
    case 'TRADE_NOT_ALLOWED':
      // Blocked pair: generic copy, never a block detail (matches trade initiation)
      return {
        ...base,
        kind: 'blocked',
        message: "You can't start a trade with this user right now.",
      };
    case 'PRIVATE_HAVE_LIST':
      // A normal state, not an error: callers hide the browse-full affordance
      return {
        ...base,
        kind: 'private_have_list',
        message: 'This trader has not made their full collection public.',
      };
    // The two allowIncomingTradeOffers rejections. DISTINCT codes on purpose:
    // the recipient case is a dead end the caller reports, while the proposer
    // case is recoverable in place — the user can turn their own setting back on
    // and continue. Collapsing them into one code would lose that difference.
    // Both are thrown by offer CREATE *and* by the candidates fetch, so a caller
    // can meet proposer_offers_disabled while merely loading the builder.
    case 'RECIPIENT_OFFERS_DISABLED':
      return {
        ...base,
        kind: 'recipient_offers_disabled',
        message: backendMessage || "This trader isn't accepting trade offers right now.",
      };
    case 'PROPOSER_OFFERS_DISABLED':
      // Callers open EnableTradeOffersModal on this rather than showing the
      // message: the dialog is the remedy, and the raw copy reads as an
      // instruction to go elsewhere.
      return {
        ...base,
        kind: 'proposer_offers_disabled',
        message:
          backendMessage || 'Turn on "Allow others to send me trade offers" to send offers.',
      };
    default:
      return base;
  }
};
